import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { getApiBaseUrl } from '@/lib/api-url'

// ============================================================
// Types
// ============================================================

interface GenerateFamilySummaryResponse {
  summary_text: string
  record_id: string
  generated_at: string
}

interface SendFamilySummaryResponse {
  success?: boolean
  message?: string
  sent_to: Array<{ phone: string; name: string }>
  notification_log_ids: string[]
}

interface NotificationContact {
  id: string
  whatsapp_number: string
  recipient_name: string
  country_code: string
}

// ============================================================
// API helper
// ============================================================

const API_BASE_URL = getApiBaseUrl()

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: sessionData } = await supabase.auth.getSession()
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${sessionData.session?.access_token}`,
  }
}

// ============================================================
// Hooks
// ============================================================

export function useGenerateFamilySummary() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      recordId: string,
    ): Promise<GenerateFamilySummaryResponse> => {
      const headers = await getAuthHeaders()
      const response = await fetch(
        `${API_BASE_URL}/api/whatsapp/records/${recordId}/generate-family-summary`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({}),
        },
      )

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.message || 'Falha ao gerar resumo para familia')
      }

      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-records'] })
    },
  })
}

export function useSendFamilySummary() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      recordId,
      approved_text,
    }: {
      recordId: string
      approved_text: string
    }): Promise<SendFamilySummaryResponse> => {
      const headers = await getAuthHeaders()
      const response = await fetch(
        `${API_BASE_URL}/api/whatsapp/records/${recordId}/send-family-summary`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ approved_text }),
        },
      )

      const result = (await response
        .json()
        .catch(() => ({}))) as SendFamilySummaryResponse

      if (!response.ok || result.success === false) {
        throw new Error(result.message || 'Falha ao enviar resumo para familia')
      }

      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-records'] })
    },
  })
}

export function usePatientNotificationContacts(patientId: string) {
  return useQuery({
    queryKey: ['patient-notification-contacts', patientId],
    queryFn: async (): Promise<NotificationContact[]> => {
      const { data, error } = await supabase
        .from('family_members')
        .select(
          `
          id,
          whatsapp_number,
          country_code,
          profiles!inner(full_name)
        `,
        )
        .eq('patient_id', patientId)
        .eq('is_primary_contact', true)
        .eq('accepts_notifications', true)

      if (error) throw error
      if (!data) return []

      return data
        .map((record) => {
          const profiles = record.profiles as
            | { full_name: string }
            | { full_name: string }[]
            | null
          const recipientName = Array.isArray(profiles)
            ? profiles[0]?.full_name || 'Desconhecido'
            : profiles?.full_name || 'Desconhecido'

          return {
            id: record.id as string,
            whatsapp_number: record.whatsapp_number as string,
            recipient_name: recipientName,
            country_code: (record.country_code as string) || '55',
          }
        })
        .filter((c) => !!c.whatsapp_number)
    },
    enabled: !!patientId,
  })
}

export type {
  NotificationContact,
  GenerateFamilySummaryResponse,
  SendFamilySummaryResponse,
}
